import React from "react"
import { useTranslation } from "react-i18next"

import {
  brand,
  chipVariants,
  neutral,
  type ChipVariant
} from "../../../components/widgets/tokens.ts"

interface BusinessDocumentStatusSummaryItem {
  /** A raw status value (or bucket key, e.g. `"other"`) — echoed back
   * verbatim to `onSelectStatus`. */
  key: string
  label: string
  count: number
  /** Optional color hint, reusing the same chip palette the KPI trend chips
   * use (`src/components/widgets/tokens.ts`). Defaults to `"muted"` when
   * omitted, so a caller that doesn't care about per-status coloring still
   * gets a clean, readable chip. */
  tone?: ChipVariant
}

interface BusinessDocumentStatusSummaryProps {
  /** e.g. "Contracts" / "Proposals" — a small label ahead of the chip row.
   * Omit to render the chips alone. */
  title?: string
  total: number
  /** Defaults to a translated "Total". */
  totalLabel?: string
  items: BusinessDocumentStatusSummaryItem[]
  /** The currently active filter; `null` (or omitted) means the "Total"
   * chip is active — i.e. no status filter applied. */
  selectedKey?: string | null
  /** Called with the clicked item's `key`, or `null` for Total/clear.
   * Clicking the already-active item chip also calls back with `null`,
   * so the row doubles as its own clear control. */
  onSelectStatus: (key: string | null) => void
  loading?: boolean
}

const CHIP_HEIGHT = 30
const SKELETON_WIDTHS = [72, 132, 110, 96]

function resolveChipColors(
  tone: ChipVariant | "brand",
  active: boolean
): { background: string; color: string } {
  if (tone === "brand") {
    return active
      ? { background: brand.teal, color: "#FFFFFF" }
      : { background: "rgba(42,125,140,.10)", color: brand.teal }
  }
  const variant = chipVariants[tone]
  return active
    ? { background: variant.fg, color: "#FFFFFF" }
    : { background: variant.bg, color: variant.fg }
}

function StatusChip({
  label,
  count,
  active,
  tone,
  onClick
}: {
  label: string
  count: number
  active: boolean
  tone: ChipVariant | "brand"
  onClick: () => void
}) {
  const { background, color } = resolveChipColors(tone, active)

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        height: CHIP_HEIGHT,
        padding: "0 12px",
        borderRadius: 999,
        border: `1px solid ${active ? "transparent" : neutral.border}`,
        background,
        color,
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1,
        cursor: "pointer",
        whiteSpace: "nowrap",
        transition:
          "background 120ms ease, color 120ms ease, border-color 120ms ease"
      }}
    >
      <span style={{ fontWeight: 800 }}>{count}</span>
      <span>{label}</span>
    </button>
  )
}

/**
 * A compact, clickable status-summary strip for a business-documents table —
 * "Contracts — 38 Total, 5 Awaiting Signature, 31 Signed, 2 Other" as a row
 * of small chips, not a dashboard tile. Deliberately generic over
 * `{key, label, count}` rather than typed to `ProposalStatusSummary` or
 * `ContractStatusSummary` directly, since those two backend shapes name
 * their buckets differently (`pending_review`/`approved` vs
 * `awaiting_signature`/`signed`) — the caller normalizes either into
 * `items` before rendering.
 *
 * Presentational only: it renders `items`/`total` and calls
 * `onSelectStatus`, and holds no fetching or filtering logic of its own.
 */
export function BusinessDocumentStatusSummary({
  title,
  total,
  totalLabel,
  items,
  selectedKey = null,
  onSelectStatus,
  loading
}: BusinessDocumentStatusSummaryProps) {
  const { t } = useTranslation()
  const resolvedTotalLabel: string =
    totalLabel ??
    t("globalFiles.businessDocuments.statusSummary.total", "Total")

  if (loading) {
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }} aria-hidden>
        {SKELETON_WIDTHS.map((width, index) => (
          <div
            key={index}
            style={{
              height: CHIP_HEIGHT,
              width,
              borderRadius: 999,
              background: neutral.light
            }}
          />
        ))}
      </div>
    )
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 8
      }}
    >
      {title ? (
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: neutral.muted,
            marginRight: 2,
            textTransform: "uppercase",
            letterSpacing: "0.04em"
          }}
        >
          {title}
        </span>
      ) : null}
      <StatusChip
        label={resolvedTotalLabel}
        count={total}
        active={selectedKey === null}
        tone="brand"
        onClick={() => onSelectStatus(null)}
      />
      {items.map((item) => (
        <StatusChip
          key={item.key}
          label={item.label}
          count={item.count}
          active={selectedKey === item.key}
          tone={item.tone ?? "muted"}
          onClick={() =>
            onSelectStatus(selectedKey === item.key ? null : item.key)
          }
        />
      ))}
    </div>
  )
}

export type {
  BusinessDocumentStatusSummaryItem,
  BusinessDocumentStatusSummaryProps
}
